import { useState } from "react";
import { useDraft } from "./shared/drafts";
import { GameLayout } from "./shared/GameLayout";
import {
  BallotTally,
  GameArt,
  PersonalOutcomeLine,
  PrivateInfo,
  SubmitButton,
  TextAction,
  draftScope,
} from "./shared/PresenterParts";
import { ImpostorEvidence } from "./shared/ImpostorEvidence";
import {
  AccusationOutcome,
  accusationResultTitle,
} from "./shared/AccusationOutcome";
import {
  offlineSeatIds,
  publicTableModel,
  publicSeatName,
} from "./public-presentation";
import { impostorEvidence } from "./impostor-evidence";
import { personalOutcome } from "./outcome";
import { type PlayProps } from "./types";
export function Impostor(props: PlayProps) {
  const { state, send } = props,
    g = state.game,
    p = g.public,
    s = g.private;
  const display = props.isDisplay || state.isSpectator;
  const model = publicTableModel(g, state.players ?? []);
  const can = (a: string) => !display && g.legalActions.includes(a);
  const voting = ["vote", "runoff"].includes(g.phase);
  const [passChoice, setPassChoice] = useState<{
    matchId: string | null;
    pass: number;
  } | null>(null);
  const pass =
    passChoice?.matchId === state.matchId
      ? Math.min(passChoice.pass, p.cluePass ?? 1)
      : (p.cluePass ?? 1);
  const scope = draftScope(state);
  const eligible: string[] = (
    s.eligibleTargetIds ??
    p.voteCandidates ??
    []
  ).filter((id: string) => id !== state.playerId);
  // The ballot draft is local to this seat and phase; a target that stops being
  // eligible is dropped, never silently replaced.
  const [draftId, setDraftId] = useDraft<string | null>(
    scope,
    "vote:target",
    null,
  );
  const selected = draftId && eligible.includes(draftId) ? draftId : null;
  const cards = impostorEvidence(
    model.seats,
    p.clues ?? [],
    Math.min(pass, p.cluePass ?? 1),
  );
  const guessStatus = `${publicSeatName(model.seats, p.accusedId)} is guessing.`;
  const title = g.finished
    ? accusationResultTitle(g)
    : voting
      ? g.phase === "runoff"
        ? "Break the tie."
        : "Who doesn’t belong?"
      : g.phase === "clue"
        ? can("clue")
          ? "Your turn."
          : `${publicSeatName(model.seats, p.turnPlayerId)} is giving a clue.`
        : can("guess")
          ? "Guess the secret word."
          : guessStatus;
  const cluesThisPass = (p.clues ?? []).filter(
    (v: { pass: number }) => v.pass === p.cluePass,
  ).length;
  const art = <GameArt kind="impostor" variant="stamp" />;
  const offline = offlineSeatIds(state.players);
  // Mirrors the engine's rule (normalised exact match) so the player hears it
  // before a round trip; the server stays authoritative.
  const norm = (v: string) =>
    v.normalize("NFC").trim().replace(/\s+/gu, " ").toLowerCase();
  const ownWord: string | undefined = s.word;
  const checkClue = (text: string) =>
    ownWord && norm(text) === norm(ownWord)
      ? "That’s your word. Give a clue that hints at it instead."
      : null;
  const outcome = personalOutcome(g, state.playerId, display);
  // Guess phase: the completed public ballots, counted onto the clue cards and
  // summarised under the title ("9 of 12 voted for you" on the accused's phone).
  const ballots: { playerId: string; targetId: string | null }[] =
    g.phase === "guess" ? (p.revealedBallots ?? []) : [];
  const voteCounts: Record<string, number> = {};
  for (const b of ballots)
    if (b.targetId) voteCounts[b.targetId] = (voteCounts[b.targetId] ?? 0) + 1;
  const voteSummary =
    ballots.length && p.accusedId
      ? !display && p.accusedId === state.playerId
        ? `${voteCounts[p.accusedId] ?? 0} of ${ballots.length} voted for you`
        : `${voteCounts[p.accusedId] ?? 0} of ${ballots.length} votes`
      : null;
  return (
    <GameLayout
      {...props}
      composition="stacked"
      title={title}
      privateScope="match"
      progressLabel="voted"
      unrevealedTitle={
        can("clue") && !(p.clues ?? []).length
          ? "Peek at your card, then give a clue"
          : undefined
      }
      contextArt={
        !g.finished && (p.cluePass ?? 1) > 1 ? (
          <div className="context-side">
            {art}
            <label className="clue-pass-label">
              <span className="sr-only">Clue pass</span>
              <select
                aria-label="Clue pass"
                value={pass}
                onChange={(e) =>
                  setPassChoice({
                    matchId: state.matchId,
                    pass: Number(e.target.value),
                  })
                }
              >
                {Array.from({ length: p.cluePass ?? 1 }, (_, i) => (
                  <option key={i} value={i + 1}>
                    Pass {i + 1}
                  </option>
                ))}
              </select>
            </label>
          </div>
        ) : (
          art
        )
      }
      publicTitle={
        g.phase === "clue"
          ? `${publicSeatName(model.seats, p.turnPlayerId)} is giving a clue.`
          : g.phase === "guess" && !g.finished
            ? guessStatus
            : title
      }
      phaseNote={
        g.finished ? null : g.phase === "guess" ? (
          voteSummary
        ) : g.phase === "clue" ? (
          `Clue ${Math.min(cluesThisPass + 1, model.seats.length)} of ${model.seats.length}`
        ) : model.progress ? (
          <span className="clue-vote-progress">
            {model.progress.submitted} of {model.progress.eligible} voted
          </span>
        ) : null
      }
      phaseLabel={
        g.finished
          ? "Result"
          : voting
            ? g.phase === "runoff"
              ? "Runoff vote"
              : "Vote"
            : g.phase === "guess"
              ? "Final guess"
              : `Clue pass ${p.cluePass ?? 1} of ${p.cluePasses ?? 1}`
      }
      board={
        <>
          {!g.finished ? (
            <ImpostorEvidence
              key={g.phase}
              cards={cards}
              actorId={
                g.phase === "clue" && pass === (p.cluePass ?? 1)
                  ? model.actorId
                  : null
              }
              viewerId={display ? null : state.playerId}
              offlineIds={offline}
              ballot={voting}
              votes={g.phase === "guess" ? voteCounts : undefined}
              selection={
                can("vote")
                  ? {
                      eligibleIds: eligible,
                      selectedId: selected,
                      disabled: props.disabled,
                      onSelect: (id) => setDraftId(id),
                    }
                  : undefined
              }
            />
          ) : null}
          {g.phase === "guess" && !g.finished && state.isDisplay ? (
            <div className="play-display-wide">
              <BallotTally
                players={p.players ?? []}
                ballots={p.revealedBallots ?? []}
                heading="The vote"
              />
            </div>
          ) : null}
          {g.finished ? (
            <>
              <PersonalOutcomeLine outcome={outcome} />
              <AccusationOutcome
                game={g}
                expanded={display}
                viewerId={display ? null : state.playerId}
              />
            </>
          ) : null}
        </>
      }
      action={
        can("clue") ? (
          <TextAction
            label="Your clue"
            type="clue"
            placeholder="One short clue…"
            send={send}
            scope={scope}
            validate={checkClue}
          />
        ) : can("vote") ? (
          <SubmitButton
            type="button"
            disabled={!selected}
            onClick={() => {
              if (selected && !props.disabled)
                send({ type: "vote", targetId: selected });
            }}
          >
            {selected
              ? `Vote for ${publicSeatName(model.seats, selected)}`
              : "Pick someone to accuse"}
          </SubmitButton>
        ) : can("guess") ? (
          <TextAction
            label="Guess the secret word"
            type="guess"
            placeholder="One final guess…"
            send={send}
            scope={scope}
          />
        ) : null
      }
      privateContent={
        <PrivateInfo state={state}>
          {s.role === "impostor" && !s.word ? (
            <p className="private-hint">
              You have no word. Listen, blend in, and guess it if you are
              caught.
            </p>
          ) : null}
        </PrivateInfo>
      }
    />
  );
}
